import { Icon } from '@iconify/react/dist/iconify.js';
import { useEffect, useRef, useState } from 'react';

interface BoxInputProps {
  value: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: number;
  className?: string;
  innerClassName?: string;
  showToolbar?: boolean;
  onSubmit?: () => void;
  autoFocus?: boolean;
  label?: string;
  isRequired?: boolean;
  error?: string;
}

const looksLikeHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s || '');

export default function BoxInput({
  value,
  onChange,
  onBlur,
  readOnly = false,
  placeholder = 'Write something…',
  minHeight = 60,
  className = '',
  innerClassName = '',
  showToolbar = true,
  onSubmit,
  autoFocus,
  label,
  isRequired,
  error,
}: BoxInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Seed the editor's HTML when first mounted or when entering edit mode.
  // We intentionally don't sync on every value change — contentEditable would
  // lose the caret on every keystroke.
  useEffect(() => {
    if (readOnly || !editorRef.current) return;
    const initial = looksLikeHtml(value)
      ? value || ''
      : (value || '').replace(/\n/g, '<br/>');
    if (editorRef.current.innerHTML !== initial) {
      editorRef.current.innerHTML = initial;
    }
    if (autoFocus) {
      editorRef.current.focus();
      // place caret at end
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  const execCommand = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const normalizeIfEmpty = () => {
    const el = editorRef.current;
    if (!el) return;
    // contentEditable leaves <br> / empty <div> behind after clearing text,
    // which prevents :empty from matching and the placeholder from returning.
    if (!el.textContent || el.textContent.trim() === '') {
      if (el.innerHTML !== '') el.innerHTML = '';
    }
  };

  const handleInput = () => {
    normalizeIfEmpty();
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  if (readOnly) {
    const isEmpty = !value || !String(value).trim();
    if (isEmpty) {
      return (
        <div
          className={`text-sm text-input-inactive-placeholder ${className}`}
        >
          {placeholder}
        </div>
      );
    }
    return looksLikeHtml(value) ? (
      <div
        className={`text-sm text-input-inactive-placeholder leading-relaxed break-words ${className}`}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    ) : (
      <div
        className={`text-sm text-input-inactive-placeholder leading-relaxed whitespace-pre-wrap break-words ${className}`}
      >
        {value}
      </div>
    );
  }

  return (
    <div className={`w-full flex flex-col gap-1 ${className}`}>
      {label && (
        <label className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-input-active-label font-semibold">
          {label}
          {isRequired && <span className="text-error-2 ml-0.5">*</span>}
        </label>
      )}
      <div
        className={`border-0.5 rounded-lg bg-modal-input transition-colors ${
          error
            ? 'border-error-2'
            : isFocused
            ? 'border-gray-3/40'
            : 'border-gray-3/20'
        }`}
      >
        <div className="p-3">
          <div
            ref={editorRef}
            contentEditable
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              normalizeIfEmpty();
              onBlur?.();
            }}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            className={`w-full bg-transparent text-sm text-text-primary-highlight outline-none break-words whitespace-pre-wrap empty:before:content-[attr(data-placeholder)] empty:before:text-input-inactive-placeholder ${innerClassName}`}
            style={{ minHeight }}
            data-placeholder={placeholder}
            suppressContentEditableWarning
          />
        </div>

        {showToolbar && (
          <div className="border-t border-gray-3/20 px-3 py-2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => execCommand('bold')}
              className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
              title="Bold"
            >
              <Icon icon="lucide:bold" width={16} height={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('italic')}
              className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
              title="Italic"
            >
              <Icon icon="lucide:italic" width={16} height={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('underline')}
              className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
              title="Underline"
            >
              <Icon icon="lucide:underline" width={16} height={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('insertUnorderedList')}
              className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
              title="Bullet list"
            >
              <Icon icon="lucide:list" width={16} height={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('insertOrderedList')}
              className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
              title="Numbered list"
            >
              <Icon icon="lucide:list-ordered" width={16} height={16} />
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-error-2 mt-1">{error}</p>}
    </div>
  );
}
